// src/services/api.js
let API_BASE_URL;
if (process.env.NODE_ENV === 'production') {
  API_BASE_URL = `${process.env.REACT_APP_API_URL}/api`;
} else {
  API_BASE_URL = 'http://localhost:5002/api';
}

/**
 * Traduce la respuesta del servidor, o su error, a algo que la pantalla pueda usar.
 *
 * El mensaje del servidor es el que sirve: «Ya cargaste la factura A-5521 de
 * J-300000001», «Una factura emitida no se puede eliminar», «El precio no puede
 * ser negativo». Antes se perdían todos: el `throw` con el mensaje bueno estaba
 * dentro del mismo `try` que envolvía el parseo, así que lo atrapaba su propio
 * `catch` y salía convertido en «Error 409: No se pudo procesar la solicitud».
 * Por eso ahora el parseo va aparte y el throw, después.
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    // El cuerpo puede no ser JSON (un 502 del proxy, una respuesta vacía). Que
    // falle el parseo no debe tapar lo que sí sabemos: el código de estado.
    let cuerpo = null;
    try {
      cuerpo = await response.json();
    } catch {
      cuerpo = null;
    }

    const mensaje = cuerpo?.error || cuerpo?.message || null;

    if (response.status === 403) {
      throw new Error(mensaje || 'Sin acceso.');
    }
    if (!mensaje) {
      throw new Error(`Error ${response.status}: No se pudo procesar la solicitud`);
    }

    // Los errores crudos de mongo no le dicen nada a quien está mirando.
    if (mensaje.includes('duplicate key') && mensaje.includes('email')) {
      throw new Error('Este correo electrónico ya está registrado en el sistema');
    }
    if (mensaje.includes('duplicate key') && mensaje.includes('rif')) {
      throw new Error('Este RIF/Cédula ya está registrado en el sistema');
    }

    throw new Error(mensaje);
  }

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    const data = await response.json();
    if (data.error) {
      throw new Error(data.error);
    }
    return data;
  }

  return null;
};

// Función para obtener las cabeceras con el token de autenticación
const getAuthHeaders = (contentType = 'application/json') => {
  const headers = { 'Content-Type': contentType };
  const token = localStorage.getItem('token');
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// Exportamos las funciones para usar en otras partes de la aplicación
export { API_BASE_URL, handleResponse, getAuthHeaders };

// Servicios para Clientes con autenticación
export const clientsApi = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/clients`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/clients`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Servicios para Productos con autenticación
export const productsApi = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/products`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Servicios para Facturas con autenticación
export const invoicesApi = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/invoices`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/invoices/${id}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/invoices`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/invoices/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/invoices/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  // Una factura emitida no se elimina: se anula. Conserva su número y su número
  // de control, y el correlativo sigue sin huecos.
  anular: async (id, motivo) => {
    const response = await fetch(`${API_BASE_URL}/invoices/${id}/anular`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ motivo })
    });
    return handleResponse(response);
  },
  // Registra la retención de IVA que un cliente le practicó a una factura.
  // Va por su propia ruta porque llega después de emitir, y la ruta de edición
  // rechaza las facturas emitidas. Con monto 0 se borra la retención.
  registrarRetencion: async (id, { monto, comprobante, porcentaje, fecha }) => {
    const response = await fetch(`${API_BASE_URL}/invoices/${id}/retencion`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ monto, comprobante, porcentaje, fecha })
    });
    return handleResponse(response);
  },
  // Nota de crédito o débito sobre una factura emitida. Lleva su propio número
  // y queda enlazada a la que ajusta.
  emitirNota: async (id, tipo, datos) => {
    const ruta = tipo === 'debit_note' ? 'nota-debito' : 'nota-credito';
    const response = await fetch(`${API_BASE_URL}/invoices/${id}/${ruta}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(datos)
    });
    return handleResponse(response);
  },
  downloadPDF: async (invoiceId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/pdf`, {
        method: 'GET',
        headers: {
          ...getAuthHeaders('application/pdf'),
          'Accept': 'application/pdf'
        }
      });

      if (!response.ok) {
        throw new Error('Error al descargar el PDF');
      }

      // Obtener el blob del PDF
      const blob = await response.blob();

      // Crear URL del blob
      const url = window.URL.createObjectURL(blob);

      // Crear link temporal para la descarga
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `factura-${invoiceId}.pdf`;

      // Agregar a documento, hacer clic y limpiar
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return true;
    } catch (error) {
      console.error('Error descargando PDF:', error);
      throw error;
    }
  }
};

// Servicios para documentos/cotizaciones con autenticación
export const documentsApi = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/documents`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/documents`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Servicios para Company con autenticación
export const companyApi = {
  get: async () => {
    const response = await fetch(`${API_BASE_URL}/company`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  update: async (data) => {
    const response = await fetch(`${API_BASE_URL}/company`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    
    // Para FormData no incluimos Content-Type, pero sí el token de autorización
    const headers = {};
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/company/logo`, {
      method: 'POST',
      headers,
      body: formData
    });
    return handleResponse(response);
  },
  deleteLogo: async (logoId) => {
    // MODIFICACIÓN: Codificamos el ID para manejar caracteres como "/"
    const encodedId = encodeURIComponent(logoId);
    
    const response = await fetch(`${API_BASE_URL}/company/logo/${encodedId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  updateTheme: async (settings) => {
    const response = await fetch(`${API_BASE_URL}/company/theme`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(settings)
    });
    return handleResponse(response);
  }
};

// Servicios para Autenticación
export const authApi = {
  getMe: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};
// Servicios para Compras (Libro de Compras, carga manual)
export const purchasesApi = {
  getAll: async (filtros = {}) => {
    const query = new URLSearchParams(
      Object.entries(filtros).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    const response = await fetch(`${API_BASE_URL}/purchases${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/purchases/${id}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/purchases`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/purchases/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/purchases/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Libros fiscales. Son de sólo lectura: se arman de lo que ya está registrado,
// así que no hay nada que crear ni editar desde acá.
const traerLibro = async (cual, { desde, hasta, pagina, porPagina } = {}) => {
  const partes = [];
  if (desde && hasta) partes.push(`desde=${desde}`, `hasta=${hasta}`);
  // Sin página se pide el libro entero, que es lo que necesita la exportación.
  if (pagina && porPagina) partes.push(`pagina=${pagina}`, `porPagina=${porPagina}`);
  const query = partes.length ? `?${partes.join('&')}` : '';
  const response = await fetch(`${API_BASE_URL}/libros/${cual}${query}`, {
    headers: getAuthHeaders()
  });
  return handleResponse(response);
};

export const librosApi = {
  ventas: (periodo) => traerLibro('ventas', periodo),
  compras: (periodo) => traerLibro('compras', periodo),
  resumen: (periodo) => traerLibro('resumen', periodo)
};
